/**
 * 選び方診断。
 *
 * 移植元: hitotoashi-brand/wp-content/themes/hitotoashi/assets/js/diagnosis.js
 * 挙動は移植元と同じ。カスタム要素（Dawn の流儀）に載せ替えただけで、
 * **判定 decide() は1文字も変えていない**。
 *
 * 設問・選択肢・結果はすべて Liquid 側で HTML に出力済み。
 * このスクリプトは「どれを見せるか」だけを切り替える（DOMを組み立てない）。
 *
 * 判定は「選び方診断（案）2026-07-23」に従う:
 *   階層3の希望が最優先で結果を確定する。
 *   「おススメから試したい」を選んだときだけ、階層1×2のマトリクスで決まる。
 *
 * ★ 設問や選択肢の値を変えるときは、必ず decide() も一緒に直すこと。
 *   ブランドサイトで、判定を差し替えたのに旧JSがブラウザキャッシュから動き続け、
 *   結果が常にフットケアになる事故を起こしている。
 *
 * ★ JS が動かないときは診断本体が hidden のままだが、
 *   <noscript> の案内と比較表（#compare）が残るので、商品も価格も読める。
 */
class HttDiagnosis extends HTMLElement {
  connectedCallback() {
    this.head = this.querySelector('[data-quiz-head]');
    this.noEl = this.querySelector('[data-quiz-no]');
    this.backBtn = this.querySelector('[data-quiz-back]');
    this.bar = this.querySelector('[data-quiz-bar]');
    this.fill = this.querySelector('[data-quiz-fill]');
    this.fair = this.querySelector('[data-quiz-fair]');
    this.results = this.querySelector('[data-quiz-results]');

    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.resultEls = Array.from(this.querySelectorAll('[data-quiz-result]'));

    if (!this.steps.length || !this.resultEls.length) return;

    this.total = this.steps.length;
    this.answers = [];

    this.addEventListener('click', (event) => {
      const pick = event.target.closest('[data-quiz-pick]');
      if (pick) {
        this.answers.push(pick.getAttribute('data-quiz-pick'));
        this.render(true);
        return;
      }

      if (event.target.closest('[data-quiz-back]')) {
        this.answers.pop();
        this.render(true);
        return;
      }

      if (event.target.closest('[data-quiz-restart]')) {
        this.answers = [];
        this.render(true);
      }
    });

    // JS が動いたのでここで初めて出す。
    this.hidden = false;
    this.render(false);
  }

  /**
   * 回答から結果キーを決める。
   *
   * answers[0] … 階層1 'sole' | 'toe' | 'posture'
   * answers[1] … 階層2 'stand' | 'life' | 'sport'
   * answers[2] … 階層3 'soft' | 'hard' | 'entry' | 'rec'
   *
   * @return {string} 'fc' | 'pt' | 'daily'
   */
  decide() {
    // 階層3で希望が示されていれば、階層1・2によらず確定する。
    switch (this.answers[2]) {
      case 'soft':
        return 'fc';
      case 'hard':
        return 'pt';
      case 'entry':
        return 'daily';
    }

    // 'rec'（おススメから試したい）は階層1×2のマトリクス。
    //   足裏・かかと → 常にフットケア。
    //   それ以外 → 「長い時間立つ・歩く」ならフットケア、日常生活／運動ならポテンシャル。
    if (this.answers[0] === 'sole') {
      return 'fc';
    }

    return this.answers[1] === 'stand' ? 'fc' : 'pt';
  }

  /**
   * 現在の状態を描画する。
   *
   * @param {boolean} moveFocus 見出しへフォーカスを移すか（初期表示では移さない）。
   */
  render(moveFocus) {
    const step = this.answers.length;
    const done = step >= this.total;

    this.steps.forEach((el, i) => {
      el.hidden = done || i !== step;
    });

    this.resultEls.forEach((el) => {
      el.hidden = true;
    });

    this.head.hidden = done;
    this.bar.hidden = done;
    this.fair.hidden = done;
    this.results.hidden = !done;

    if (done) {
      const target = this.querySelector('[data-quiz-result="' + this.decide() + '"]');
      if (target) {
        target.hidden = false;

        // 理由文は階層1の答えに合う1本だけ出す（'*' は常に出す）。
        target.querySelectorAll('[data-quiz-reason]').forEach((el) => {
          const rk = el.getAttribute('data-quiz-reason');
          el.hidden = !(rk === '*' || rk === this.answers[0]);
        });

        if (moveFocus) {
          const heading = target.querySelector('.htt-dg__card-name');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus();
          }
        }
      }
      return;
    }

    this.noEl.textContent = String(step + 1);
    this.backBtn.hidden = step === 0;

    const pct = Math.round((step / this.total) * 100);
    this.fill.style.width = pct + '%';
    this.bar.setAttribute('aria-valuenow', String(pct));

    if (moveFocus) {
      const q = this.steps[step].querySelector('.htt-dg__q');
      if (q) q.focus();
    }
  }
}

if (!customElements.get('htt-diagnosis')) {
  customElements.define('htt-diagnosis', HttDiagnosis);
}
